'use client';

import { useMemo } from 'react';
import { useCart, useHybridCartPrices } from '@kidslabs/module-quote';
import { authStore$ } from '@kidslabs/module-core';
import type { VoucherEvaluationContext } from '../utils';
import { useCustomerOrderStats } from './use-customer-order-stats';

/**
 * Builds the evaluation context needed by the voucher condition evaluator.
 *
 * Data sources:
 * - Cart subtotal: grand_total minus shipping from useHybridCartPrices (module-quote).
 *   This matches the "Tổng tiền sản phẩm" displayed in CartInformation and aligns with
 *   Magento's base_subtotal used for voucher condition evaluation on the backend.
 *   Previously used useCartTotal().subtotal (sum of row_total) which could differ
 *   from the actual grand_total when cart-level discounts or pricing rules are applied.
 * - Cart item SKUs: extracted from cart items via useCart (module-quote)
 * - Cart total quantity: sum of all item quantities for total_qty_expr conditions
 * - Cart item categories: collected from product category data for category_ids conditions
 * - Customer order stats: pre-indexed from KidsLabs_CustomerOrderStats Magento module
 *   (O(1) lookup via kidsplaza_customer_order_stats table)
 *
 * Authentication is automatically detected from the auth store observable.
 * Order stats are only fetched for authenticated users; guests get default zeros.
 *
 * Performance notes:
 * - Cart data is reactive: changes trigger re-evaluation automatically
 * - Order stats use cache-and-network policy to avoid blocking renders
 * - All values are memoized to prevent unnecessary re-renders
 */
export const useVoucherEvaluationContext = (): VoucherEvaluationContext => {
    const { items } = useCart();
    const { prices, shippingFee } = useHybridCartPrices();
    const isAuthenticated = authStore$.isAuthenticated.get() ?? false;
    const orderStats = useCustomerOrderStats(isAuthenticated);

    const cartSubtotal = useMemo(() => {
        const rawGrandTotal = prices.grand_total?.value;
        if (rawGrandTotal == null) {
            return 0;
        }
        return rawGrandTotal - (shippingFee?.amount ?? 0);
    }, [prices.grand_total?.value, shippingFee?.amount]);

    const cartItemSkus = useMemo(
        () => items.map(item => item.product?.sku).filter((sku): sku is string => !!sku),
        [items]
    );

    const cartTotalQuantity = useMemo(() => items.reduce((sum, item) => sum + (item.quantity ?? 0), 0), [items]);

    const cartItemQuantities = useMemo(
        () =>
            items.reduce<Record<string, number>>((acc, item) => {
                const sku = item.product?.sku;
                if (sku) {
                    acc[sku] = (acc[sku] ?? 0) + (item.quantity ?? 0);
                }
                return acc;
            }, {}),
        [items]
    );

    const cartItemCategories = useMemo(() => {
        const categories: string[] = [];
        items.forEach(item => {
            const productCategories = item.product?.categories;
            if (productCategories) {
                productCategories.forEach(cat => {
                    if (cat?.id != null) {
                        categories.push(String(cat.id));
                    }
                });
            }
        });
        return [...new Set(categories)];
    }, [items]);

    return useMemo<VoucherEvaluationContext>(
        () => ({
            cartSubtotal,
            cartItemSkus,
            customerCompletedOrders: orderStats.completedOrderCount,
            customerAppOrders: orderStats.appOrderCount,
            customerTotalOrders: orderStats.totalOrderCount,
            customerSalesAmount: orderStats.totalSalesAmount,
            cartTotalQuantity,
            cartItemQuantities,
            cartItemCategories,
        }),
        [
            cartSubtotal,
            cartItemSkus,
            orderStats.completedOrderCount,
            orderStats.appOrderCount,
            orderStats.totalOrderCount,
            orderStats.totalSalesAmount,
            cartTotalQuantity,
            cartItemQuantities,
            cartItemCategories,
        ]
    );
};
